import { useActiveSession } from '@texas/hooks/useSession';
import { useWidgetLayout, WidgetOption } from '../widgets/useWidgetLayout';
import { TableColumns } from './TableColumns';
import {
  getDefaultConceptWidgetsLayout,
  getDefaultCustomerWidgetsLayout,
} from './defaultCustomerWidgetsLayout';
import {
  defaultArticlesWidgetOptions,
  defaultPaWidgetOptions,
  pageSize,
} from './widgetOptions/defaultWidgetsOptions';
import { useTranslation } from 'react-i18next';
import { BodyOverride } from '../shared/BodyOverride';
import { WidgetType } from '../widgets/shared/types';
import { EntityType } from '@texas/api/endpoints/trackerApi';
import { Flex } from '@chakra-ui/react';
import { PaTableColumns } from '../dashboard/PaTableColumns';
import { useMemo } from 'react';
import { DashboardToolbar } from '../dashboard/DashboardToolbar';
import { CreateArticleButton } from '../article/create/CreateArticleButton';
import { CopyArticleButton } from '../article/copy/CopyArticleButton';
import { NewConcept } from './newConcept/NewConcept';

const localStorageCustomerSuffix = 'c';
const localStorageConceptSuffix = 'concept';

export function Customer({
  customerId,
  conceptId,
  conceptCustomerId,
}: {
  customerId: number;
  conceptId: number | undefined;
  conceptCustomerId?: number;
}) {
  const { t } = useTranslation();
  const session = useActiveSession();
  const actionCustomerId = conceptId ? conceptCustomerId : customerId;

  const widgetOptions = useMemo<WidgetOption>(
    () => ({
      [WidgetType.TrackInquiries]: {
        type: WidgetType.TrackInquiries,
        localStorageKey: undefined,
        trackerPointer: conceptId ?? customerId,
        entityType: conceptId
          ? EntityType.Concept
          : customerId
          ? EntityType.Customer
          : null,
      },
      [WidgetType.TrackSamples]: {
        type: WidgetType.TrackSamples,
        localStorageKey: undefined,
        persistColumnVisibility: false,
        trackerPointer: conceptId ?? customerId,
        entityType: conceptId
          ? EntityType.Concept
          : customerId
          ? EntityType.Customer
          : null,
        hideStopFollowing: true,
      },
      [WidgetType.Articles]: {
        type: WidgetType.Articles,
        ...defaultArticlesWidgetOptions(customerId, conceptId),
        localStorageKey: localStorageKey(
          session.currentUser.id,
          customerId,
          conceptId,
        ),
        columns: TableColumns,
        customWidgetTitle: t('general.brandArticles'),
        pageSize: pageSize,
        optOutDefaultOrdering: true,
        persistColumnVisibility: false,
      },
      [WidgetType.PaRejectionReasons]: {
        type: WidgetType.PaRejectionReasons,
        customerId: customerId,
        componentSettings: {
          editWidget: 'hidden',
          notDraggable: true,
        },
      },
      [WidgetType.ArticlesCount]: {
        type: WidgetType.ArticlesCount,
        ...defaultArticlesWidgetOptions(customerId, conceptId),
        localStorageKey: localStorageKey(
          session.currentUser.id,
          customerId,
          conceptId,
        ),
        columns: TableColumns,
        optOutDefaultOrdering: true,
      },
      [WidgetType.ProductApprovalsCount]: {
        type: WidgetType.ProductApprovalsCount,
        ...defaultPaWidgetOptions(customerId),
        localStorageKey: localStorageKey(
          session.currentUser.id,
          customerId,
          conceptId,
        ),
        columns: PaTableColumns,
        optOutDefaultOrdering: true,
      },
      [WidgetType.ProductApprovalLeadTime]: {
        type: WidgetType.ProductApprovalLeadTime,
        customerId: customerId,
      },
      [WidgetType.ProductApprovalRatio]: {
        type: WidgetType.ProductApprovalRatio,
        customerId: customerId,
      },
      [WidgetType.ArticleProgress]: {
        type: WidgetType.ArticleProgress,
        customerId: customerId,
      },
      [WidgetType.PaLeadTimeDistribution]: {
        type: WidgetType.PaLeadTimeDistribution,
        customerIds: [customerId],
      },
    }),
    [session.currentUser.id, customerId, conceptId, t],
  );

  const board = conceptId ? 'Concept' : 'Customer';
  const defaultLayout = useMemo(
    () =>
      conceptId
        ? getDefaultConceptWidgetsLayout()
        : getDefaultCustomerWidgetsLayout(),
    [conceptId],
  );

  const { widgetComponents, addNewWidget, widgets, layout, setLayout } =
    useWidgetLayout({
      localStorageKey: localStorageKey(
        session.currentUser.id,
        customerId,
        conceptId,
      ),
      board: board,
      defaultLayout,
      widgetOptions,
    });

  return (
    <BodyOverride>
      <Flex flexDir="column" gap={2}>
        <Flex className="dashboard-actions">
          <CreateArticleButton
            customerId={actionCustomerId}
            conceptId={conceptId}
          />
          <CopyArticleButton
            customerId={actionCustomerId}
            conceptId={conceptId}
            variant="entityDirectorySecondary"
          />
          <NewConcept
            customerId={actionCustomerId}
            buttonProps={{ variant: 'entityDirectorySecondary' }}
          />
          <DashboardToolbar
            board={board}
            currentWidgets={widgets}
            onAdd={(type, name, color) => addNewWidget(type, name, color)}
            layout={layout}
            setLayout={setLayout}
            widgetOptions={widgetOptions}
            defaultLayout={defaultLayout}
          />
        </Flex>
        {widgetComponents}
      </Flex>
    </BodyOverride>
  );
}

function localStorageKey(
  userId: number,
  customerId: number | undefined,
  conceptId: number | undefined,
) {
  return customerId
    ? `${userId}_${customerId}_${localStorageCustomerSuffix}`
    : conceptId
    ? `${userId}_${conceptId}_${localStorageConceptSuffix}`
    : undefined;
}
